import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Heart,
  ArrowRight,
  Bed,
  Bath,
  LandPlot,
  MapPin,
  MessageCircle,
  FileText,
  ShieldCheck,
  CheckCircle2,
  PlusCircle,
  Scale,
} from 'lucide-react';
import { Property } from '../types';
import { useLanguage } from '../context/LanguageContext';
import { useCurrency } from '../context/CurrencyContext';
import { useAuth } from '../context/AuthContext';
import { useCompare } from '../context/CompareContext';
import { isDemoProperty } from '../lib/demoPropertyHelper';
import { isListingPublic, getListingVerificationInfo, getPropertyAreaSqm } from '../lib/listingStatusHelper';
import { AuthModal } from './AuthModal';
import { buildPropertyUrl } from '../lib/routes';
import { CardImageCarousel } from './CardImageCarousel';
import { buildBuyerWhatsAppMessage } from '../lib/leadTemplates';
import { fetchListingContact } from '../lib/contactService';
import { getListingTitle } from '../lib/adTranslationHelper';

interface FeaturedPropertiesProps {
  properties?: Property[];
  isLoading?: boolean;
  onSelectProperty?: (property: Property) => void;
  onViewAll?: () => void;
  onOpenMap?: () => void;
  selectedFilter?: string;
  onFilterChange?: (filter: string) => void;
}

export const FeaturedProperties: React.FC<FeaturedPropertiesProps> = ({
  properties = [],
  isLoading = false,
  onSelectProperty,
  onViewAll,
}) => {
  const { language } = useLanguage();
  const isFr = language === 'fr';
  const navigate = useNavigate();
  const { currency, formatPrice, formatXafWithCode } = useCurrency();
  const { user, userProfile } = useAuth();
  const { addToCompare, removeFromCompare, isComparing } = useCompare();

  const [favorites, setFavorites] = useState<Record<string, boolean>>(() => {
    if (typeof window === 'undefined') return {};
    try {
      const stored = localStorage.getItem('cameroon_favorites');
      if (stored) {
        const ids: string[] = JSON.parse(stored);
        const map: Record<string, boolean> = {};
        ids.forEach((id) => {
          map[id] = true;
        });
        return map;
      }
    } catch {}
    return {};
  });
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalPrompt, setAuthModalPrompt] = useState<string | undefined>(undefined);
  const [pendingAuthAction, setPendingAuthAction] = useState<(() => void) | null>(null);

  const toggleFavorite = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    e.preventDefault();
    setFavorites((prev) => {
      const isFav = !prev[id];
      const next = { ...prev, [id]: isFav };
      try {
        const stored = localStorage.getItem('cameroon_favorites');
        const ids: string[] = stored ? JSON.parse(stored) : [];
        const nextIds = isFav
          ? Array.from(new Set([...ids, id]))
          : ids.filter((item) => item !== id);
        localStorage.setItem('cameroon_favorites', JSON.stringify(nextIds));
      } catch {}
      return next;
    });
  };

  // Filter strictly real, verified/published properties from Cloud Firestore (Zero Mock/Zero Fake)
  const realFeatured = (properties || [])
    .filter((p) => !isDemoProperty(p) && isListingPublic(p))
    .sort((a, b) => {
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;
      return 0;
    });

  const displayProperties = realFeatured.slice(0, 6);

  return (
    <section className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 sm:mb-8 min-h-[36px]">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight whitespace-nowrap">
            {isFr ? 'Propriétés en vedette' : 'Featured properties'}
          </h2>
          {realFeatured.length > 0 && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-extrabold bg-emerald-100 text-[#094426] border border-emerald-200 shadow-2xs">
              {realFeatured.length} {isFr ? (realFeatured.length === 1 ? 'annonce active' : 'annonces actives') : (realFeatured.length === 1 ? 'active ad' : 'active ads')}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onViewAll}
          className="text-[#b45309] hover:text-[#92400e] text-xs sm:text-sm font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
        >
          <span>{isFr ? 'Voir tous les biens' : 'View all properties'}</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* Property Cards or Clean Authentic Empty State */}
      {isLoading && displayProperties.length === 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {[1, 2, 3].map((n) => (
                <div key={n} className="bg-white rounded-2xl border border-slate-200/90 overflow-hidden shadow-xs p-3 animate-pulse">
                  <div className="h-44 bg-slate-200 rounded-xl mb-3" />
                  <div className="h-4 bg-slate-200 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-slate-100 rounded w-1/2 mb-3" />
                  <div className="h-6 bg-slate-200 rounded w-1/3" />
                </div>
              ))}
            </div>
          ) : displayProperties.length === 0 ? (
            <div className="bg-white border border-slate-200/90 rounded-2xl p-7 sm:p-9 flex flex-col items-center justify-center text-center shadow-xs">
              <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-[#094426] flex items-center justify-center mb-3">
                <LandPlot className="w-6 h-6 text-[#094426]" />
              </div>
              <h3 className="text-base sm:text-lg font-serif font-bold text-slate-900 mb-1.5">
                {isFr ? 'Aucune annonce en vedette actuellement' : 'No featured listings at the moment'}
              </h3>
              <p className="text-xs text-slate-500 max-w-sm mb-5 leading-relaxed">
                {isFr
                  ? 'Soyez le premier propriétaire, agence ou notaire agréé à publier votre terrain ou propriété au Cameroun.'
                  : 'Be the first owner, agency, or notary to list land or real estate in Cameroon.'}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2.5">
                <button
                  type="button"
                  onClick={() => navigate(isFr ? '/fr/deposer-annonce' : '/en/post-property')}
                  className="px-4 py-2 bg-[#094426] hover:bg-[#0c5932] text-white rounded-xl text-xs font-bold transition flex items-center gap-1.5 shadow-xs cursor-pointer"
                >
                  <PlusCircle className="w-4 h-4 text-emerald-300" />
                  <span>{isFr ? 'Déposer une annonce' : 'Post a Property'}</span>
                </button>
                <button
                  type="button"
                  onClick={onViewAll}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer"
                >
                  <span>{isFr ? 'Toutes les annonces' : 'View all listings'}</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 lg:gap-7">
              {displayProperties.map((prop) => {
              const propUrl = buildPropertyUrl(prop, language);
              const isFav = Boolean(favorites[prop.id]);
              const vInfo = getListingVerificationInfo(prop, isFr);
              const areaSqm = getPropertyAreaSqm(prop);
              const rawPrice = prop.priceRaw || 0;
              const pricePerSqm =
                prop.type === 'land' && areaSqm && areaSqm > 0 && rawPrice > 0
                  ? Math.round(rawPrice / areaSqm).toLocaleString(isFr ? 'fr-FR' : 'en-US')
                  : null;

              return (
                <article
                  key={prop.id}
                  onClick={() => {
                    if (onSelectProperty) {
                      onSelectProperty(prop);
                    } else {
                      navigate(propUrl);
                    }
                  }}
                  className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-xs hover:shadow-lg transition-all duration-200 flex flex-col group cursor-pointer"
                >
                  {/* 4:3 Image Container with Multi-Photo Swipe Carousel & Accessible Touch Targets */}
                  <div className="relative aspect-[4/3] w-full overflow-hidden bg-slate-100">
                    <CardImageCarousel
                      mainImage={prop.image}
                      images={prop.images}
                      alt={getListingTitle(prop, language)}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    {vInfo.status === 'verified' && (
                      <div className="absolute top-3 left-3 bg-[#094426]/95 text-white text-xs font-bold px-2.5 py-1 rounded-lg flex items-center gap-1.5 backdrop-blur-xs shadow-sm z-10">
                        <CheckCircle2 className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                        <span>{vInfo.badgeText}</span>
                      </div>
                    )}
                    {vInfo.status === 'pending' && (
                      <div className="absolute top-3 left-3 bg-amber-700/95 text-white text-xs font-bold px-2.5 py-1 rounded-lg flex items-center gap-1.5 backdrop-blur-xs shadow-sm z-10">
                        <ShieldCheck className="w-3.5 h-3.5 text-amber-200 shrink-0" />
                        <span>{vInfo.badgeText}</span>
                      </div>
                    )}
                    {/* Compare Button */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (isComparing(prop.id)) {
                          removeFromCompare(prop.id);
                        } else {
                          addToCompare(prop);
                        }
                      }}
                      className={`absolute top-3 right-14 h-9 px-2.5 rounded-full backdrop-blur-xs flex items-center gap-1 text-xs font-bold transition-all shadow-sm cursor-pointer z-10 ${
                        isComparing(prop.id)
                          ? 'bg-amber-400 text-slate-950 font-black ring-2 ring-white scale-105'
                          : 'bg-white/90 text-slate-700 hover:text-slate-950 hover:bg-white'
                      }`}
                      title={isComparing(prop.id) ? (isFr ? 'Retirer du comparateur' : 'Remove from compare') : (isFr ? 'Comparer ce bien' : 'Compare property')}
                    >
                      <Scale className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">{isComparing(prop.id) ? (isFr ? 'Comparé' : 'Added') : (isFr ? 'Comparer' : 'Compare')}</span>
                    </button>

                    <button
                      type="button"
                      onClick={(e) => toggleFavorite(e, prop.id)}
                      className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 hover:bg-white backdrop-blur-xs flex items-center justify-center text-slate-700 hover:text-red-600 transition-colors shadow-sm cursor-pointer z-10"
                      aria-label="Save Property"
                    >
                      <Heart
                        className={`w-4 h-4 transition-colors ${
                          isFav ? 'fill-red-500 text-red-500' : ''
                        }`}
                      />
                    </button>
                  </div>

                  {/* Card Body: Price First -> Title -> Location -> Specs & WhatsApp CTA */}
                  <div className="p-4 flex flex-col flex-1 justify-between min-h-[148px]">
                    <div>
                      {/* Line 1: Bold Price + XAF/m² Badge */}
                      <div className="flex items-baseline justify-between gap-2 flex-wrap">
                        <div className="flex items-baseline gap-1.5 flex-wrap">
                          <div className="text-lg sm:text-[19px] font-extrabold text-slate-900 tracking-tight">
                            {currency === 'XAF'
                              ? formatXafWithCode(prop.priceRaw || prop.price, isFr)
                              : formatPrice(prop.priceRaw || prop.price, isFr).primary}
                          </div>
                          {currency !== 'XAF' && (
                            <span className="text-[11px] font-semibold text-slate-400">
                              ({formatXafWithCode(prop.priceRaw || prop.price, isFr)})
                            </span>
                          )}
                        </div>
                        {pricePerSqm && (
                          <span className="text-xs font-bold text-[#094426] bg-emerald-50 border border-emerald-200/80 px-2 py-0.5 rounded-md">
                            {pricePerSqm} XAF/m²
                          </span>
                        )}
                      </div>

                      {/* Line 2: Property Title */}
                      <h3 className="text-[15px] font-semibold text-slate-800 group-hover:text-[#094426] transition-colors line-clamp-1 leading-snug mt-1.5">
                        <Link
                          to={propUrl}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onSelectProperty) {
                              onSelectProperty(prop);
                            }
                          }}
                          className="hover:underline"
                        >
                          {getListingTitle(prop, language)}
                        </Link>
                      </h3>

                      {/* Line 3: Location with Pin */}
                      <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-1 truncate">
                        <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span className="truncate">{prop.location}</span>
                      </div>
                    </div>

                    {/* Line 4: Specs Row + Accessible WhatsApp CTA Pill */}
                    <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-2 text-xs text-slate-600">
                      <div className="flex items-center gap-2.5 flex-wrap font-medium">
                        {/* House/Apartment Specs */}
                        {prop.specs.beds !== undefined && (
                          <span className="flex items-center gap-1">
                            <Bed className="w-3.5 h-3.5 text-slate-400" />
                            <span>{prop.specs.beds} {isFr ? 'ch' : 'beds'}</span>
                          </span>
                        )}
                        {prop.specs.baths !== undefined && (
                          <span className="flex items-center gap-1">
                            <Bath className="w-3.5 h-3.5 text-slate-400" />
                            <span>{prop.specs.baths} {isFr ? 'sdb' : 'baths'}</span>
                          </span>
                        )}
                        {/* Area */}
                        {prop.specs.area && (
                          <span className="flex items-center gap-1">
                            <LandPlot className="w-3.5 h-3.5 text-slate-400" />
                            <span>{prop.specs.area}</span>
                          </span>
                        )}
                        {/* Land Title Deed Indicator */}
                        {prop.type === 'land' && (
                          <span className="flex items-center gap-1 text-emerald-800 font-semibold">
                            <FileText className="w-3.5 h-3.5 text-emerald-600" />
                            <span>{isFr ? 'Titre foncier' : 'Title deed'}</span>
                          </span>
                        )}
                      </div>

                      {/* Auth-gated WhatsApp Direct Chat Pill - WCAG AA #16A34A */}
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!user && !userProfile) {
                            setAuthModalPrompt(
                              isFr
                                ? 'Veuillez vous connecter pour contacter ce vendeur sur WhatsApp.'
                                : 'Please sign in to contact this seller on WhatsApp.'
                            );
                            setPendingAuthAction(() => async () => {
                              try {
                                const contact = await fetchListingContact(prop.id, { lang: isFr ? 'fr' : 'en' });
                                if (contact.whatsappUrl) {
                                  import('../lib/firestoreService').then(({ trackPropertyLeadConversion }) => {
                                    trackPropertyLeadConversion(prop.id, 'whatsapp', 'featured_card');
                                  }).catch(() => {});
                                  window.open(contact.whatsappUrl, '_blank', 'noopener,noreferrer');
                                }
                              } catch (err) {
                                console.error('Contact error:', err);
                              }
                            });
                            setShowAuthModal(true);
                            return;
                          }

                          try {
                            const contact = await fetchListingContact(prop.id, { lang: isFr ? 'fr' : 'en' });
                            if (contact.whatsappUrl) {
                              import('../lib/firestoreService').then(({ trackPropertyLeadConversion }) => {
                                trackPropertyLeadConversion(prop.id, 'whatsapp', 'featured_card');
                              }).catch(() => {});
                              window.open(contact.whatsappUrl, '_blank', 'noopener,noreferrer');
                            }
                          } catch (err: any) {
                            console.error('Contact error:', err);
                          }
                        }}
                        className="h-8 px-2.5 rounded-xl bg-[#16A34A] hover:bg-[#15803D] text-white text-xs font-bold flex items-center justify-center gap-1 transition-colors shadow-2xs shrink-0 cursor-pointer"
                        title={isFr ? 'Contacter sur WhatsApp' : 'Contact on WhatsApp'}
                      >
                        <MessageCircle className="w-3.5 h-3.5 fill-white" />
                        <span>WhatsApp</span>
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
          )}

      {/* Auth Modal if unauthenticated user clicks WhatsApp */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => {
          setShowAuthModal(false);
          setPendingAuthAction(null);
        }}
        promptMessage={authModalPrompt}
        onLoginSuccess={() => {
          setShowAuthModal(false);
          if (pendingAuthAction) {
            const action = pendingAuthAction;
            setPendingAuthAction(null);
            action();
          }
        }}
      />
    </section>
  );
};
